// src/shared/permissions.js
// Store reactivo + helper global para APLICAR los permisos CRUD/botones
// (estándar del rol + personalización del usuario) en menú y vistas.
//
// Uso:
//   main.js    -> import Permissions from '@/shared/permissions'; Vue.use(Permissions);
//   cargar     -> this.$perms.setFromResources(effectiveResources, { roleCode, hasCustomization })
//   en vista   -> v-if="$can('journal', 'create')"
//   en menú    -> this.$perms.canRead(uri)
//
// Si el mapa está VACÍO (no se cargó / usuario admin sin restricción),
// `can()` devuelve true por defecto para no bloquear de más (fail-open).
// Cambia DEFAULT_ALLOW a false si prefieres fail-closed (oculta todo si no cargó).
//
// ACCESO TOTAL "QUEMADO" POR ROL:
//   Los roles en FULL_ACCESS_ROLES (por code) obtienen acceso total por defecto,
//   SIN depender de lo que mande el ACL. Esto resuelve el caso en que el ACL no
//   envía los códigos de los botones de acción. PERO si ese usuario tiene
//   personalización (hasCustomization = true), se RESPETA su mapa y ya no se
//   aplica el acceso total.

import Vue from 'vue';

const DEFAULT_ALLOW = true;

// Codes de rol con acceso total por defecto (estándar). 'ACCOUNTANT' = CONTADOR.
const FULL_ACCESS_ROLES = ['ACCOUNTANT'];

const store = new Vue({
	data() {
		return {
			ready: false,
			// map: { '<uri>': ['read', 'create', '<btnCode>', ...] }
			map: {},
			// Code del rol del usuario actual (p.ej. 'ACCOUNTANT').
			roleCode: null,
			// ¿El usuario tiene personalización propia? Si es true, se respeta el
			// mapa y NO se aplica el acceso total por rol.
			hasCustomization: false,
		};
	},
	methods: {
		// Carga el mapa efectivo del usuario: { uri: [acciones] }
		// meta (opcional): { roleCode, hasCustomization }
		setFromResources(resources, meta = {}) {
			this.map = resources && typeof resources === 'object' ? resources : {};
			this.ready = true;
			if (Object.prototype.hasOwnProperty.call(meta, 'roleCode')) {
				this.roleCode = meta.roleCode || null;
			}
			if (Object.prototype.hasOwnProperty.call(meta, 'hasCustomization')) {
				this.hasCustomization = !!meta.hasCustomization;
			}
		},
		// Setea (o actualiza) el rol y si tiene personalización, por separado.
		setRole(roleCode, hasCustomization = false) {
			this.roleCode = roleCode || null;
			this.hasCustomization = !!hasCustomization;
		},
		clear() {
			this.map = {};
			this.ready = false;
			this.roleCode = null;
			this.hasCustomization = false;
		},
		// ¿Este usuario tiene acceso total por rol estándar (sin personalización)?
		isFullAccess() {
			return (
				FULL_ACCESS_ROLES.indexOf(this.roleCode) > -1 &&
				!this.hasCustomization
			);
		},
		// ¿El usuario tiene `action` en el recurso `uri`?
		can(uri, action) {
			// Rol estándar con acceso total (y sin personalización) -> todo permitido.
			if (this.isFullAccess()) {
				return true;
			}
			if (!this.ready || Object.keys(this.map).length === 0) {
				return DEFAULT_ALLOW;
			}
			const acts = this.map[uri];
			return !!(acts && acts.indexOf(action) > -1);
		},
		// Atajo para la acción maestra (Ver). Sin read, el recurso no se muestra.
		canRead(uri) {
			return this.can(uri, 'read');
		},
	},
});

export const permissionsStore = store;

// DEBUG: expone el store en consola para diagnosticar (window.permsDebug)
if (typeof window !== 'undefined') {
	window.permsDebug = store;
}

export default {
	install(VueInstance) {
		/* eslint-disable no-param-reassign */
		// $perms -> store completo (setFromResources, setRole, can, canRead, clear, ready, map)
		VueInstance.prototype.$perms = store;
		// $can('<uri>', '<accion>') -> boolean (reactivo en templates)
		VueInstance.prototype.$can = (uri, action) => store.can(uri, action);
		/* eslint-enable no-param-reassign */
	},
};



// WEBPACK FOOTER //
// ./src/shared/permissions.js